import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Globe,
  Plus,
  Search,
  CheckCircle2,
  XCircle,
  ChevronRight,
  AlertTriangle,
  Calculator,
  Clock,
  Shield,
  ShieldCheck,
  Image,
  X,
} from "lucide-react";
import { api, Domain } from "@/lib/api";
import { useAuthStore } from "@/stores/auth";
import { useDataStore } from "@/stores/data";
import { Modal } from "@/components/ui/Modal";

type PolicyFilter = "all" | "none" | "quarantine" | "reject";

// Tier limits for domain counts
const TIER_LIMITS: Record<string, { domains: number; maxDomains?: number; overagePrice?: number }> = {
  free: { domains: 1 },
  starter: { domains: 5 },
  pro: { domains: 25, maxDomains: 100, overagePrice: 5 },
  enterprise: { domains: 250 },
  enterprise_plus: { domains: Infinity },
};

export function Domains() {
  const { organization, activeClientOrg } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [policyFilter, setPolicyFilter] = useState<PolicyFilter>("all");
  const [showAddModal, setShowAddModal] = useState(false);

  // Handle redirect filter params (enforcement, bimi)
  const redirectFilter = searchParams.get("filter");
  const [showRedirectBanner, setShowRedirectBanner] = useState(!!redirectFilter);

  // Clear the filter param from URL after showing the banner
  const dismissRedirectBanner = () => {
    setShowRedirectBanner(false);
    setSearchParams({}, { replace: true });
  };

  async function refreshDomains() {
    try {
      setLoading(true);
      // Use the data store so other components (like AppShell) get updated
      const store = useDataStore.getState();
      const domainData = await store.refreshDomains();
      setDomains(domainData);
    } catch (error) {
      console.error("Failed to fetch domains:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function fetchDomains() {
      try {
        setLoading(true);
        // Use the data store for initial load too
        const store = useDataStore.getState();
        const domainData = await store.ensureDomains();
        if (cancelled) return;
        setDomains(domainData);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to fetch domains:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchDomains();

    // Listen for MSP client context changes
    const handleClientChange = () => {
      fetchDomains();
    };
    window.addEventListener("msp-client-changed", handleClientChange);

    return () => {
      cancelled = true;
      window.removeEventListener("msp-client-changed", handleClientChange);
    };
  }, [activeClientOrg?.id]);

  // Helper to get domain's current policy
  const getDomainPolicy = (d: Domain) => d.current_policy || d.dmarc_policy || "none";

  const filteredDomains = domains.filter((domain) => {
    const matchesSearch = domain.domain_name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesPolicy =
      policyFilter === "all" || getDomainPolicy(domain) === policyFilter;
    return matchesSearch && matchesPolicy;
  });

  const policyStats = {
    all: domains.length,
    none: domains.filter((d) => getDomainPolicy(d) === "none").length,
    quarantine: domains.filter((d) => getDomainPolicy(d) === "quarantine").length,
    reject: domains.filter((d) => getDomainPolicy(d) === "reject").length,
  };

  const tier = organization?.billing_tier || organization?.tier || "free";
  const tierLimit = TIER_LIMITS[tier] || TIER_LIMITS.free;
  const domainCount = domains.length;
  const isAtLimit = domainCount >= tierLimit.domains && tier !== "enterprise_plus";
  const isProTier = tier === "pro";
  const isOverBase = isProTier && domainCount > 25;
  const canAddMore = tier === "enterprise_plus" || domainCount < (tierLimit.maxDomains || tierLimit.domains);

  // Calculate overage cost for Pro tier
  const calculateOverageCost = (count: number) => {
    if (!isProTier || count <= 25) return 0;
    return (count - 25) * (tierLimit.overagePrice || 5);
  };

  const currentOverage = calculateOverageCost(domainCount);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800">Domains</h1>
          <p className="text-sm md:text-base text-slate-500">
            Manage your domains and monitor their DMARC status.
          </p>
        </div>
        <div className="flex items-center gap-3 md:gap-4">
          {/* Domain Counter */}
          <div className="text-right">
            <p className="text-xs md:text-sm text-slate-500">Domains Used</p>
            <p className={`text-base md:text-lg font-semibold ${isAtLimit && !canAddMore ? "text-red-600" : isOverBase ? "text-amber-600" : "text-slate-800"}`}>
              {domainCount} / {tierLimit.domains === Infinity ? "∞" : tierLimit.maxDomains || tierLimit.domains}
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            disabled={!canAddMore}
            className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 bg-amber-500 text-slate-900 rounded-lg text-sm md:text-base font-medium hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Add Domain</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Pro Tier Overage Banner */}
      {isProTier && domainCount >= 20 && (
        <div className={`rounded-xl p-4 ${isOverBase ? "bg-amber-50 border border-amber-200" : "bg-blue-50 border border-blue-200"}`}>
          <div className="flex items-start gap-3">
            <Calculator className={`w-5 h-5 mt-0.5 ${isOverBase ? "text-amber-600" : "text-blue-500"}`} />
            <div className="flex-1">
              <h3 className={`font-medium ${isOverBase ? "text-amber-800" : "text-blue-700"}`}>
                {isOverBase ? "Domain Overage Active" : "Approaching Base Limit"}
              </h3>
              <p className={`text-sm mt-1 ${isOverBase ? "text-amber-700" : "text-blue-700"}`}>
                {isOverBase
                  ? `You have ${domainCount - 25} domains beyond your base 25. Additional billing: $${currentOverage}/month.`
                  : `You're using ${domainCount} of 25 included domains. Additional domains cost $5/each/month (up to 100 total).`
                }
              </p>
              {isOverBase && (
                <div className="mt-3 p-3 bg-white rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Base plan (25 domains)</span>
                    <span className="font-medium">$99.00</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-600">Overage ({domainCount - 25} × $5)</span>
                    <span className="font-medium">${currentOverage}.00</span>
                  </div>
                  <div className="border-t mt-2 pt-2 flex justify-between">
                    <span className="font-medium text-slate-800">Monthly Total</span>
                    <span className="font-bold text-slate-800">${99 + currentOverage}.00</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Limit Reached Banner */}
      {!canAddMore && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-800">
                {domainCount} {domainCount === 1 ? "domain" : "domains"} monitored. Your next domain is waiting.
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                {isProTier
                  ? "You've reached the maximum of 100 domains on Pro. Enterprise supports 250 domains with API access, SSO, and APEX analytics."
                  : tier === "starter"
                    ? "Starter includes 5 domains. Pro scales to 100 with one-click remediation, vendor reports, and APEX links."
                    : "Every domain you add gets the same monitoring, alerts, and remediation. Do not leave domains unprotected."
                }
              </p>
              <Link
                to="/settings/billing"
                className="inline-flex items-center gap-1 mt-3 px-4 py-2 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors"
              >
                {tier === "free" ? "Start Free Trial" : isProTier ? "Upgrade to Enterprise" : "Upgrade to Pro"} &rarr;
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Redirect Info Banner */}
      {showRedirectBanner && redirectFilter === "enforcement" && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-blue-500 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-blue-700">Enforcement is now domain-centric</h3>
              <p className="text-sm text-blue-700 mt-1">
                Click on any domain and go to the <strong>Enforcement</strong> tab to view enforcement readiness, blockers, and advance your policy.
              </p>
            </div>
            <button
              onClick={dismissRedirectBanner}
              className="p-1 text-blue-500 hover:text-blue-500 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showRedirectBanner && redirectFilter === "bimi" && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Image className="w-5 h-5 text-purple-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-purple-800">BIMI is now domain-centric</h3>
              <p className="text-sm text-purple-600 mt-1">
                Click on any domain and go to the <strong>BIMI</strong> tab to configure your brand logo and VMC certificate.
              </p>
            </div>
            <button
              onClick={dismissRedirectBanner}
              className="p-1 text-purple-500 hover:text-purple-600 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search domains..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
        </div>

        {/* Policy Filter */}
        <div className="flex flex-wrap gap-2">
          {(["all", "none", "quarantine", "reject"] as PolicyFilter[]).map(
            (policy) => (
              <button
                key={policy}
                onClick={() => setPolicyFilter(policy)}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  policyFilter === policy
                    ? "bg-amber-500 text-slate-900"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {policy === "all" ? "All" : policy.charAt(0).toUpperCase() + policy.slice(1)} ({policyStats[policy]})
              </button>
            )
          )}
        </div>
      </div>

      {/* Domain List */}
      {filteredDomains.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            {domains.length === 0 ? "No domains yet" : "No matching domains"}
          </h3>
          <p className="text-slate-500 mb-6">
            {domains.length === 0
              ? "Add your first domain to start monitoring DMARC reports."
              : "Try adjusting your search or filter criteria."}
          </p>
          {domains.length === 0 && (
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Domain
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Domain
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Policy
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    SPF
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    DKIM
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Added
                  </th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredDomains.map((domain) => (
                  <DomainRow key={domain.id} domain={domain} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Domain Modal */}
      <AddDomainModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          refreshDomains();
        }}
        currentDomainCount={domainCount}
        tier={tier}
      />
    </div>
  );
}

function DomainRow({ domain }: { domain: Domain }) {
  const policyColors = {
    none: "bg-slate-100 text-slate-600",
    quarantine: "bg-orange-100 text-orange-700",
    reject: "bg-emerald-100 text-emerald-700",
  };

  // Get days until expiry for unverified domains
  const daysUntilExpiry = domain.days_until_expiry as number | null | undefined;
  const isExpiringSoon = domain.verification_status === "pending" && daysUntilExpiry !== null && daysUntilExpiry !== undefined && daysUntilExpiry <= 5;
  const isExpiryCritical = daysUntilExpiry !== null && daysUntilExpiry !== undefined && daysUntilExpiry <= 2;

  return (
    <tr className={`hover:bg-slate-50 transition-colors ${isExpiryCritical ? "bg-red-50" : isExpiringSoon ? "bg-amber-50" : ""}`}>
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isExpiryCritical ? "bg-red-100" : isExpiringSoon ? "bg-amber-100" : "bg-slate-100"}`}>
            <Globe className={`w-4 h-4 ${isExpiryCritical ? "text-red-600" : isExpiringSoon ? "text-amber-600" : "text-slate-600"}`} />
          </div>
          <div>
            <span className="font-medium text-slate-800">{domain.domain_name}</span>
            {isExpiringSoon && (
              <div className={`flex items-center gap-1 text-xs mt-0.5 ${isExpiryCritical ? "text-red-600" : "text-amber-600"}`}>
                <Clock className="w-3 h-3" />
                <span>
                  {daysUntilExpiry === 0
                    ? "Expires today - verify now!"
                    : `Expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? "" : "s"}`}
                </span>
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        {domain.verification_status === "verified" ? (
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <CheckCircle2 className="w-4 h-4" />
            Verified
          </span>
        ) : isExpiryCritical ? (
          <span className="inline-flex items-center gap-1 text-red-600">
            <AlertTriangle className="w-4 h-4" />
            Expiring
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-slate-400">
            <XCircle className="w-4 h-4" />
            Pending
          </span>
        )}
      </td>
      <td className="px-6 py-4">
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            policyColors[(domain.current_policy || domain.dmarc_policy) as keyof typeof policyColors] ||
            policyColors.none
          }`}
        >
          {domain.current_policy || domain.dmarc_policy || "none"}
        </span>
      </td>
      <td className="px-6 py-4">
        {domain.spf_configured ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
        ) : (
          <XCircle className="w-5 h-5 text-red-600" />
        )}
      </td>
      <td className="px-6 py-4">
        {domain.dkim_configured ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
        ) : (
          <XCircle className="w-5 h-5 text-red-600" />
        )}
      </td>
      <td className="px-6 py-4 text-slate-500 text-sm">
        {new Date(domain.created_at).toLocaleDateString()}
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-1">
          <Link
            to={`/domains/${domain.id}?tab=enforcement`}
            className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors inline-block"
            title="View Enforcement"
          >
            <Shield className="w-4 h-4" />
          </Link>
          <Link
            to={`/domains/${domain.id}`}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors inline-block"
          >
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </td>
    </tr>
  );
}

interface AddDomainModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentDomainCount?: number;
  tier?: string;
}

function AddDomainModal({ open, onClose, onSuccess, currentDomainCount = 0, tier = "free" }: AddDomainModalProps) {
  const [domainName, setDomainName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isProTier = tier === "pro";
  const newDomainCount = currentDomainCount + 1;
  const willExceedBase = isProTier && newDomainCount > 25;
  const newOverageCost = willExceedBase ? (newDomainCount - 25) * 5 : 0;
  const currentOverageCost = isProTier && currentDomainCount > 25 ? (currentDomainCount - 25) * 5 : 0;
  const additionalCost = newOverageCost - currentOverageCost;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!domainName.trim()) return;

    try {
      setLoading(true);
      setError(null);
      await api.createDomain(domainName.trim());
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add domain");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Modal.Content title="Add Domain" size="md">
        <div className="p-4 pt-3">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="domain"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Domain Name
              </label>
              <input
                id="domain"
                type="text"
                placeholder="example.com"
                value={domainName}
                onChange={(e) => setDomainName(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                disabled={loading}
              />
            </div>

            {/* Pro Tier Overage Notice */}
            {isProTier && willExceedBase && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-orange-800">Overage billing applies</p>
                    <p className="text-orange-700 mt-1">
                      This will be domain #{newDomainCount}, exceeding your base 25.
                      {additionalCost > 0 && (
                        <span className="block mt-1">
                          Additional cost: <strong>+${additionalCost}/month</strong>
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <p className="text-red-600 text-sm mb-4">{error}</p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors disabled:opacity-50"
                disabled={loading || !domainName.trim()}
              >
                {loading ? "Adding..." : willExceedBase ? `Add Domain (+$${additionalCost}/mo)` : "Add Domain"}
              </button>
            </div>
          </form>
        </div>
      </Modal.Content>
    </Modal>
  );
}